// src/pages/document/DocumentVersionListPage.tsx
// Version history for a specific document.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import {
  Card,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
  Badge,
} from '@react/components';
import { useScope } from '@/hooks/useScope';
import {
  documentStatusMap,
  getStatusMapping,
  formatDate,
  getInitials,
  getAvatarColor,
} from '@/features/document/logic';

export const DocumentVersionListPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useDocument();
  const scope = useScope();
  const { t, locale } = useI18n();
  const [items, setItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [docTitle, setDocTitle] = useState('');

  // Load document title for breadcrumb
  useEffect(() => {
    if (!id) return;
    store.GetDocument(id).then((doc: any) => {
      setDocTitle(doc?.title || '');
    }).catch(() => {});
  }, [id, scope.productId]);

  // Load versions for this specific document
  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await store.ListVersions('document', id);
        // Filter to only versions belonging to this document (safety net)
        const allVersions = res?.versions?.versions || [];
        const filtered = allVersions.filter((v: any) => {
          const targetId = v?.target?.id || v?.document_id || v?.entity?.product_id;
          // Keep all if no target info (trust the API), or match document id
          return !targetId || targetId === id;
        });
        setItems(filtered);
      } catch (err: unknown) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <nav className="text-sm mb-2">
          <Link to="/documents" className="text-muted-foreground hover:text-foreground">
            {t('document.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/documents/${id}`} className="text-muted-foreground hover:text-foreground">
            {docTitle || t('document.details')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('document.versions')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{t('document.versions')}</h1>
        {docTitle && (
          <p className="text-sm text-muted-foreground mt-1">{docTitle}</p>
        )}
      </div>

      {error ? (
        <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />
      ) : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('document.version.number')}</TableHead>
              <TableHead>{t('document.version.label')}</TableHead>
              <TableHead>{t('document.fieldStatus')}</TableHead>
              <TableHead>{t('document.version.changeSummary')}</TableHead>
              <TableHead>{t('document.version.approvedBy')}</TableHead>
              <TableHead>{t('document.version.approvedAt')}</TableHead>
              <TableHead>{t('document.fieldCreatedAt')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableEmpty colSpan={7} message={t('document.version.noVersions')} />
            ) : (
              items.map((item: any) => {
                const versionId = item.entity?.id || item.id;
                const status = item.status || '';
                const statusInfo = getStatusMapping(documentStatusMap, status);
                const approver = item.approved_by?.display_name || item.approved_by?.name || '';

                return (
                  <TableRow key={versionId || Math.random()}>
                    {/* Version number */}
                    <TableCell>
                      <Link
                        to={`/documents/${id}/versions/${versionId}`}
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        {item.version_number ?? '--'}
                      </Link>
                    </TableCell>

                    {/* Version label */}
                    <TableCell>
                      <span className="text-sm font-mono text-foreground">
                        {item.version_label || '--'}
                      </span>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      {status ? (
                        <Badge variant={statusInfo.variant as any} size="sm">
                          {t(statusInfo.labelKey)}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">--</span>
                      )}
                    </TableCell>

                    {/* Change summary */}
                    <TableCell>
                      <span className="text-sm text-muted-foreground truncate max-w-xs block">
                        {item.change_summary || '--'}
                      </span>
                    </TableCell>

                    {/* Approved by */}
                    <TableCell>
                      {approver ? (
                        <div className="flex items-center gap-2">
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-semibold text-white flex-shrink-0"
                            style={{ backgroundColor: getAvatarColor(approver) }}
                          >
                            {getInitials(approver)}
                          </div>
                          <span className="text-sm">{approver}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">--</span>
                      )}
                    </TableCell>

                    {/* Approved at */}
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(item.approved_at, locale)}
                      </span>
                    </TableCell>

                    {/* Created at */}
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(item.created_at, locale)}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default DocumentVersionListPage;
